import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, Check, Star, Calendar, FileText, Image, 
  CreditCard, Zap, QrCode, Link2, Palette, BarChart3,
  MessageSquare, Shield, ArrowRight, Users, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProDemo = () => {
  const navigate = useNavigate();

  const produtos = [
    { icon: FileText, nome: 'Orçamento na Hora', desc: 'PDFs profissionais em segundos' },
    { icon: Calendar, nome: 'Agenda Cheia', desc: 'Agendamento + lembretes WhatsApp' },
    { icon: Image, nome: 'Meu Trampo', desc: 'Portfólio + avaliações de clientes' },
    { icon: Users, nome: 'ClienteJá', desc: 'CRM + cobranças + campanhas' },
    { icon: Sparkles, nome: 'PostaJá', desc: 'Conteúdo para redes com IA' },
  ];

  const benefits = [
    { icon: Image, text: 'Portfólio profissional ilimitado', included: true },
    { icon: FileText, text: 'Orçamentos ilimitados', included: true },
    { icon: Calendar, text: 'Agendamentos ilimitados', included: true },
    { icon: Users, text: 'Clientes ilimitados (ClienteJá)', included: true },
    { icon: Sparkles, text: '48 conteúdos/mês (PostaJá Plus)', included: true },
    { icon: CreditCard, text: 'Pagamento integrado', included: true },
    { icon: MessageSquare, text: 'Lembretes e campanhas WhatsApp', included: true },
    { icon: Shield, text: 'Badge "Pro Verificado"', included: true, pro: true },
    { icon: BarChart3, text: 'Relatórios e métricas', included: true, pro: true },
    { icon: Link2, text: 'Link curto personalizado', included: true, pro: true },
    { icon: QrCode, text: 'QR Code para cartão', included: true, pro: true },
    { icon: Palette, text: 'Temas exclusivos', included: true, pro: true },
  ];

  const comparativo = [
    { item: 'Orçamento na Hora', separado: 'R$ 29,99', pro: '✓' },
    { item: 'Agenda Cheia', separado: 'R$ 39,99', pro: '✓' },
    { item: 'Meu Trampo', separado: 'R$ 39,99', pro: '✓' },
    { item: 'ClienteJá Plus', separado: 'R$ 89,99', pro: '✓' },
    { item: 'PostaJá Plus', separado: 'R$ 79,99', pro: '✓' },
    { item: 'Total separado', separado: 'R$ 279,95', pro: '-' },
    { item: 'AtivaJá Pro', separado: '-', pro: 'R$ 199,99' },
    { item: 'Economia', separado: '-', pro: 'R$ 79,96/mês' },
  ];

  const flow = [
    { step: 1, title: 'Portfólio', desc: 'Cliente vê seus trabalhos e avaliações', color: 'purple' },
    { step: 2, title: 'Orçamento', desc: 'Solicita orçamento do serviço', color: 'blue' },
    { step: 3, title: 'Agendamento', desc: 'Escolhe melhor data e horário', color: 'green' },
    { step: 4, title: 'Pagamento', desc: 'Paga sinal ou valor total', color: 'amber' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-slate-950 to-amber-900/20" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 rounded-full border border-amber-500/30 mb-6">
            <Crown className="text-amber-400" size={18} />
            <span className="text-amber-300 font-medium">AtivaJá Pro</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Tudo que você precisa em{' '}
            <span className="bg-gradient-to-r from-amber-400 to-purple-400 bg-clip-text text-transparent">
              um só lugar
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Todos os 5 produtos AtivaJá em um só pacote. 
            Economize R$ 79,96/mês pagando junto.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={() => navigate('/pro/configurar')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-lg px-8 py-6"
            >
              <Crown size={20} />
              Começar agora
              <ArrowRight size={20} />
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.open('/pro/demo', '_blank')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 text-lg px-8 py-6"
            >
              Ver demonstração
            </Button>
          </div>

          <div className="inline-flex items-baseline gap-2">
            <span className="text-5xl font-bold text-white">R$ 199</span>
            <span className="text-2xl text-slate-400">,99/mês</span>
          </div>
          <p className="text-slate-500 mt-2">Todos os 5 produtos • Cancele quando quiser</p>
        </div>
      </section>

      {/* 5 Produtos */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            5 produtos em 1 assinatura
          </h2>
          <p className="text-slate-400 text-center mb-12">
            Tudo o que você precisa para profissionalizar seu negócio
          </p>

          <div className="grid sm:grid-cols-5 gap-4">
            {produtos.map((prod, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center hover:border-amber-500/50 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4">
                  <prod.icon className="text-amber-400" size={28} />
                </div>
                <h3 className="font-semibold text-white mb-2">{prod.nome}</h3>
                <p className="text-sm text-slate-400">{prod.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fluxo */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Jornada completa do seu cliente
          </h2>
          <p className="text-slate-400 text-center mb-12">
            Do primeiro contato até o pagamento, tudo automatizado
          </p>

          <div className="grid sm:grid-cols-4 gap-4">
            {flow.map((item, i) => (
              <div key={i} className="relative">
                <div className={`bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center hover:border-${item.color}-500/50 transition-all`}>
                  <div className={`w-12 h-12 rounded-full bg-${item.color}-500/20 flex items-center justify-center mx-auto mb-4`}>
                    <span className={`text-${item.color}-400 font-bold text-xl`}>{item.step}</span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden sm:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="text-slate-600" size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Tudo incluído no Pro
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((benefit, i) => (
              <div 
                key={i}
                className={`flex items-center gap-4 p-4 rounded-xl ${
                  benefit.pro 
                    ? 'bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20' 
                    : 'bg-slate-800/50 border border-slate-700'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  benefit.pro ? 'bg-amber-500/20' : 'bg-slate-700'
                }`}>
                  <benefit.icon size={20} className={benefit.pro ? 'text-amber-400' : 'text-slate-400'} />
                </div>
                <span className="text-white">{benefit.text}</span>
                {benefit.pro && (
                  <span className="ml-auto text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
                    Exclusivo
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativo */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Por que escolher o Pro?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Individual */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-2">Planos Individuais</h3>
              <p className="text-slate-400 mb-4">Cada ferramenta separada</p>
              <div className="text-3xl font-bold text-white mb-6">
                R$ 279,95<span className="text-lg text-slate-400">/mês</span>
              </div>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-slate-500" />
                  Orçamento na Hora - R$ 29,99
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-slate-500" />
                  Agenda Cheia - R$ 39,99
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-slate-500" />
                  Meu Trampo - R$ 39,99
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-slate-500" />
                  ClienteJá Plus - R$ 89,99
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-slate-500" />
                  PostaJá Plus - R$ 79,99
                </li>
                <li className="flex items-center gap-2 text-slate-500">
                  <span className="w-4 h-0.5 bg-slate-600" />
                  5 links diferentes
                </li>
              </ul>
            </div>

            {/* Pro */}
            <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/10 border-2 border-amber-500/30 rounded-2xl p-6 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-500 to-purple-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                Economize R$ 79,96/mês
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                <Crown className="text-amber-400" size={20} />
                AtivaJá Pro
              </h3>
              <p className="text-slate-400 mb-4">Todos os 5 produtos + benefícios exclusivos</p>
              <div className="text-3xl font-bold text-white mb-6">
                R$ 199,99<span className="text-lg text-slate-400">/mês</span>
              </div>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-amber-400" />
                  Orçamento + Agenda + Portfólio
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-amber-400" />
                  ClienteJá (clientes ilimitados)
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-amber-400" />
                  PostaJá Plus (48 conteúdos/mês)
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-amber-400" />
                  Link único: ativaja.com/voce
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-amber-400" />
                  Badge Pro Verificado
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/pro/configurar')}
                className="w-full mt-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                Assinar Pro
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Crown className="text-amber-400 mx-auto mb-6" size={48} />
          <h2 className="text-3xl font-bold text-white mb-4">
            Pronto para profissionalizar seu negócio?
          </h2>
          <p className="text-slate-400 mb-8">
            Configure tudo em menos de 10 minutos e comece a receber clientes hoje mesmo.
          </p>
          <Button 
            onClick={() => navigate('/pro/configurar')}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-lg px-8"
          >
            <Crown size={20} />
            Começar configuração
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProDemo;
